// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState } from 'react';

const SolitaireGame: React.FC = () => {
  const [leaderboardConfig, setLeaderboardConfig] = useState({
    "id": "solitaire_leaderboard",
    "authoritative": true,
    "sort_order": "asc",
    "operator": "best",
    "reset_schedule": "0 0 * * 1",
    "metadata": {
      "game": "solitaire",
      "description": "Solitaire game leaderboard - fastest completion times",
      "game_variants": [
        "klondike",
        "spider",
        "freecell",
        "pyramid",
        "tripeaks"
      ],
      "scoring_type": "time_based",
      "reward_tiers": {
        "1": { "coins": 200, "title": "Solitaire Speed Demon" },
        "2": { "coins": 150, "title": "Solitaire Ace" },
        "3": { "coins": 100, "title": "Solitaire Pro" },
        "top_10": { "coins": 50, "title": "Solitaire Player" }
      },
      "season_duration": "weekly",
      "min_games_completed": 10,
      "time_bonuses": {
        "under_60s": 1.5,
        "under_120s": 1.3,
        "under_180s": 1.1
      },
      "difficulty_multipliers": {
        "easy": 1.0,
        "medium": 1.2,
        "hard": 1.5,
        "expert": 2.0
      }
    }
  });

  const [jsonError, setJsonError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const handleJsonChange = (value: string) => {
    try {
      const parsed = JSON.parse(value);
      setLeaderboardConfig(parsed);
      setJsonError(null);
    } catch (error) {
      setJsonError('Invalid JSON format');
    }
  };

  const saveLeaderboardConfig = () => {
    // Here you would typically save to your backend
    console.log('Saving Solitaire leaderboard config:', leaderboardConfig);
    setIsEditing(false);
    alert('Leaderboard configuration saved successfully!');
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h1>🃏 Solitaire Game</h1>
        <p>Welcome to the Solitaire game interface!</p>
        <p>This is where the Solitaire game would be embedded or managed.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'start' }}>
        {/* Game Features */}
        <div style={{ 
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>
          <h2>Game Features</h2>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            <li>🃏 Classic Solitaire</li>
            <li>🎯 Multiple Variants</li>
            <li>⏱️ Timed Challenges</li>
            <li>🏆 Best Times</li>
            <li>📊 Statistics Tracking</li>
            <li>🪙 Completion Rewards</li>
            <li>🎨 Custom Themes</li>
          </ul>
        </div>

        {/* Leaderboard Configuration */}
        <div style={{
          background: 'white',
          border: '1px solid #e9ecef',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0, color: '#333' }}>🏆 Leaderboard Configuration</h2>
            <button
              onClick={() => setIsEditing(!isEditing)}
              style={{
                background: isEditing ? '#dc3545' : '#f093fb',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              {isEditing ? 'Cancel' : 'Edit'}
            </button>
          </div>

          {isEditing ? (
            <div>
              <textarea
                value={JSON.stringify(leaderboardConfig, null, 2)}
                onChange={(e) => handleJsonChange(e.target.value)}
                style={{
                  width: '100%',
                  height: '400px',
                  fontFamily: 'Monaco, Consolas, monospace',
                  fontSize: '0.85rem',
                  padding: '1rem',
                  border: jsonError ? '2px solid #dc3545' : '1px solid #ced4da',
                  borderRadius: '6px',
                  resize: 'vertical'
                }}
                placeholder="Enter leaderboard configuration JSON..."
              />
              {jsonError && (
                <div style={{ color: '#dc3545', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                  {jsonError}
                </div>
              )}
              <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem' }}>
                <button
                  onClick={saveLeaderboardConfig}
                  disabled={!!jsonError}
                  style={{
                    background: jsonError ? '#6c757d' : '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    cursor: jsonError ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Save Configuration
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Leaderboard ID:</strong> {leaderboardConfig.id}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Type:</strong> {leaderboardConfig.authoritative ? 'Authoritative' : 'Client'}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Sort Order:</strong> {leaderboardConfig.sort_order.toUpperCase()} (Best Times First)
              </div>
              <pre style={{
                background: '#f8f9fa',
                padding: '1rem',
                borderRadius: '6px',
                fontSize: '0.8rem',
                overflow: 'auto',
                maxHeight: '300px',
                border: '1px solid #e9ecef'
              }}>
                {JSON.stringify(leaderboardConfig, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SolitaireGame;
